/**
 * Detect correction-style prompts in the session log and cluster them.
 *
 * A "correction" is a prompt where the user is course-correcting the AI:
 *   - "no, don't do that"
 *   - "stop using emojis"
 *   - "actually, the file is at X"
 *   - "instead of X, use Y"
 *
 * The heuristic is intentionally conservative — we'd rather miss a correction
 * than flag every "no" as one. The user reviews the result anyway via
 * `cai learn review`.
 */
import { createHash } from "node:crypto";
import type { SessionEntry } from "./recorder.js";

const NEGATION_RE = /\b(no|nope|nein|stop|don'?t|never|do not|nicht)\b/i;
const REINSTRUCT_RE = /\b(instead|actually|i (told|said) you|remember to|always|please don'?t|stop doing)\b/i;
const REVERT_RE = /\b(undo|revert|go back|original|previous version)\b/i;

export interface Correction {
  ts: number;
  prompt: string;
  signal: "negation" | "reinstruct" | "revert";
  stack?: string;
}

export interface CorrectionCluster {
  /** Stable 6-char hash of the signature — usable as a CLI argument */
  id: string;
  /** Normalized signature (lowercased, no punctuation) used for grouping */
  signature: string;
  /** Most representative prompt from the cluster */
  example: string;
  count: number;
  signal: Correction["signal"];
  firstSeen: number;
  lastSeen: number;
}

/** Stable cluster identifier — same signature always gives the same id. */
function clusterId(signature: string): string {
  return createHash("sha1").update(signature).digest("hex").slice(0, 6);
}

const MIN_CLUSTER_SIZE = 2;
const MIN_PROMPT_LEN = 4;
const MAX_PROMPT_LEN_FOR_DETECTION = 280;

/**
 * Detect correction prompts. Conservative — short, sharp prompts only.
 *
 * `opts.stack` filters by the entry's recorded stack (e.g. "package.json")
 * so a "stop using emojis" correction in a Python project doesn't get
 * promoted to a rule in your Go project.
 *
 * Entries without a recorded stack always pass the filter — old logs and
 * non-CAI sources stay usable.
 */
export function detectCorrections(
  entries: SessionEntry[],
  opts: { stack?: string } = {}
): Correction[] {
  const corrections: Correction[] = [];
  for (const e of entries) {
    if (opts.stack && e.stack && e.stack !== opts.stack) continue;

    const text = e.prompt.trim();
    if (text.length < MIN_PROMPT_LEN || text.length > MAX_PROMPT_LEN_FOR_DETECTION) continue;

    let signal: Correction["signal"] | null = null;
    if (REVERT_RE.test(text)) signal = "revert";
    else if (REINSTRUCT_RE.test(text)) signal = "reinstruct";
    else if (NEGATION_RE.test(text)) signal = "negation";

    if (signal) corrections.push({ ts: e.ts, prompt: text, signal, stack: e.stack });
  }
  return corrections;
}

/**
 * Normalize a prompt for clustering: lowercase, strip punctuation, collapse
 * whitespace, drop common filler. Two prompts with the same normalized form
 * cluster together.
 */
export function normalizeForCluster(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\b(please|bitte|just|the|a|an|to|for|that|this|it|its|de|der|die|das|den)\b/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/** Cluster corrections by normalized signature, return clusters with count ≥ MIN_CLUSTER_SIZE. */
export function clusterCorrections(corrections: Correction[]): CorrectionCluster[] {
  const byKey = new Map<string, Correction[]>();
  for (const c of corrections) {
    const key = normalizeForCluster(c.prompt);
    if (!key) continue;
    const list = byKey.get(key) ?? [];
    list.push(c);
    byKey.set(key, list);
  }

  const clusters: CorrectionCluster[] = [];
  for (const [signature, list] of byKey) {
    if (list.length < MIN_CLUSTER_SIZE) continue;

    // Pick the shortest example as the representative — likely the cleanest expression
    const example = list.slice().sort((a, b) => a.prompt.length - b.prompt.length)[0];

    clusters.push({
      id: clusterId(signature),
      signature,
      example: example.prompt,
      count: list.length,
      signal: example.signal,
      firstSeen: Math.min(...list.map((l) => l.ts)),
      lastSeen: Math.max(...list.map((l) => l.ts)),
    });
  }

  return clusters.sort((a, b) => b.count - a.count);
}

/**
 * Find a cluster by its short id within the current correction set.
 * Returns null if no match. Used by `cai learn write-rule <id>`.
 */
export function findClusterById(clusters: CorrectionCluster[], id: string): CorrectionCluster | null {
  return clusters.find((c) => c.id === id) ?? null;
}

/**
 * Append a learned rule to CLAUDE.md under a managed section.
 * The section is wrapped in cai:learn-start / cai:learn-end markers so we can
 * find it again to append more rules without duplicating headings.
 */
export function appendLearnedRule(claudeMdContent: string, rule: string): string {
  const SECTION_START = "<!-- cai:learn-start -->";
  const SECTION_END = "<!-- cai:learn-end -->";
  const SECTION_HEADER = "## Learned Rules";
  const SECTION_NOTE = "_Added by `cai learn write-rule` from recurring corrections you gave Claude. Edit or remove freely._";

  // If the section already exists, insert the new rule before the end marker
  if (claudeMdContent.includes(SECTION_START) && claudeMdContent.includes(SECTION_END)) {
    return claudeMdContent.replace(SECTION_END, `${rule}\n${SECTION_END}`);
  }

  // Otherwise create the section at the end of the file
  const trimmed = claudeMdContent.replace(/\n+$/, "");
  return `${trimmed}\n\n${SECTION_START}\n\n${SECTION_HEADER}\n\n${SECTION_NOTE}\n\n${rule}\n\n${SECTION_END}\n`;
}

/** Suggest a CLAUDE.md rule line for a cluster. Just a starting point — user edits. */
export function suggestRule(cluster: CorrectionCluster): string {
  // Very simple: turn the example into an imperative-flavored bullet
  const text = cluster.example.trim().replace(/[.!?]+$/, "");
  return `- ${text}`;
}
